import React, { useMemo } from 'react'
import { TransitionPresets } from '@react-navigation/stack'
import { Platform, StyleSheet } from 'react-native'
import { useTheme } from 'styled-components/native'

import { Colors } from 'APP/Themes'
import { popToNative } from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'

import Header, { HeaderWithoutBottomPadding, HeaderProps } from 'APP/Converse/Header'
import { ResponsiveView } from 'APP/Converse/Layout'
import HomeBannerButton from 'APP/Components/HomeBannerButton'
import { TabBarButton } from 'APP/Nav/Components/TabBarButton'
import { tabBarBadgeStyle } from 'APP/Nav/Components/Badge'

import { tabBarStyles } from './style'

type ScreenHeaderCallbackProps = HeaderProps & { options: HeaderProps }

export const screenOptions = {
  ...TransitionPresets.SlideFromRightIOS,
  headerShown: false,
  freezeOnBlur: true,
  animation: 'slide_from_right',
  __statusBarStyle: 'auto',
  header: (props: ScreenHeaderCallbackProps) => (
    <ResponsiveView>
      <Header {...props} {...props.options} />
    </ResponsiveView>
  ),
  contentStyle: {
    zIndex: 0,
  },
  stackMenuStyle: {
    zIndex: 1,
  },
  ...Platform.select({ web: { animationEnabled: false } }),
}

export const screenOptionsForModals = {
  ...TransitionPresets.ModalPresentationIOS,
  ...screenOptions,
  presentation: 'modal',
  headerShown: true,
  gestureEnabled: true,
  header: (props: ScreenHeaderCallbackProps) => <Header {...props} {...props.options} isModal />,
  headerTransparent: false,
  headerShadowVisible: true,
  cardShadowEnabled: true,
  cardOverlayEnabled: true,
  __statusBarStyle: 'light',
  detachPreviousScreen: false,
}

export const screenOptionsForTransparentModals = {
  ...screenOptionsForModals,
  presentation: 'containedTransparentModal',
  animation: 'fade',
  headerShown: false,
  stackMenuStyle: {
    zIndex: -1,
  },
  contentStyle: {
    ...StyleSheet.flatten(StyleSheet.absoluteFill),
    zIndex: 0,
    ...Platform.select({
      web: {
        position: 'fixed',
        pointerEvents: 'all',
        backdropFilter: ' blur(2px)',
      },
    }),
  },
}

export const screenOptionsForBottomSheetModals = {
  ...screenOptionsForModals,
  headerShown: false,
  presentation: 'transparentModal',
  cardOverlayEnabled: true,
  gestureResponseDistance: 1000,
}

const TAB_BAR_BASE_HEIGHT = 56

export const useScreenOptionsForTabs = () => {
  const { insets } = useTheme()
  return useMemo(
    () => ({
      ...tabBarStyles,
      tabBarStyle: {
        ...tabBarStyles.tabBarStyle,
        height: TAB_BAR_BASE_HEIGHT + insets.bottom,
      },
      tabBarBadgeStyle,
      tabBarActiveTintColor: Colors.tabSelected,
      tabBarInactiveTintColor: Colors.tabUnselected,
      tabBarLabelPosition: 'below-icon',
      tabBarButton: TabBarButton,
      __statusBarStyle: 'auto',
      header: (props: ScreenHeaderCallbackProps) => (
        <ResponsiveView>
          {isEmbeddedEnvironment() && <HomeBannerButton onPress={() => popToNative(true)} />}
          <HeaderWithoutBottomPadding {...props} {...props.options} />
        </ResponsiveView>
      ),
      hideBackButton: true,
      headerShown: true,
      freezeOnBlur: true,
      accessibilityRole: 'button',
      accessibilityState: ({ focused }: { focused: boolean }) => {
        return focused ? { selected: true } : {}
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insets.bottom]
  )
}

export const screenOptionsForDrawers = {
  ...TransitionPresets.ModalPresentationIOS,
  ...screenOptions,
  presentation: 'modal',
  headerShown: true,
  gestureEnabled: true,
  header: (props: ScreenHeaderCallbackProps) => <Header {...props} {...props.options} isDrawer />,
  headerTransparent: false,
  headerShadowVisible: true,
  cardShadowEnabled: true,
  cardOverlayEnabled: true,
  __statusBarStyle: 'light',
  detachPreviousScreen: false,
}
